import { createStore } from 'zustand';
import { devtools } from 'zustand/middleware';

import type { BasePollingService, LbActions, LbPost, LbState, LbUpdate } from '../types';
import { LbStatus, LbUpdateType } from '../types';
import { sortFunction } from '../utils';
import logger from '../utils/logger';

const processUpdate = (items: LbPost[], update: LbUpdate) => {
  switch (update.action) {
    case LbUpdateType.ADD_POST:
      {
        if (items.some((p) => p.id === update.postId)) return items;

        items.unshift({
          ...update.body,
          isNew: true,
        });
      }
      break;
    case LbUpdateType.UPDATE_POST:
      {
        const index = items.findIndex((p) => p.id === update.postId);
        if (index < 0) return items;
        items[index] = update.body;
      }
      break;
    case LbUpdateType.DELETE_POST:
      {
        items = items.filter((p) => p.id !== update.postId);
      }
      break;
    default:
      break;
  }

  return items;
};

export const initialState: LbState = {
  items: [],
  status: LbStatus.EMPTY,
  lastVersion: null,
};

export const createLbStore = (initial: LbState) =>
  createStore<LbState & LbActions>()(
    devtools(
      (set, get) => ({
        ...initial,
        showAddedPosts: () =>
          set(
            (state) => {
              if (state.items.every((p) => !p.isNew)) return state;

              return {
                items: state.items.map((p) => ({ ...p, isNew: false })),
              };
            },
            false,
            'showAddedPosts',
          ),
        loadPosts: async (service: BasePollingService) => {
          const { items } = get();
          set(
            (state) => ({ status: state.items.length ? LbStatus.LOADING_MORE : LbStatus.LOADING }),
            false,
            'loadPosts - Loading',
          );

          try {
            const result = await service.loadPosts(items.at(-1)?.id);

            const debug = service.options?.debug ? logger.error : () => undefined;
            debug('Loaded posts', result.items);

            const loadedPosts = result?.items?.filter((newPost) => !items.some((p) => p.id === newPost.id));
            if (!loadedPosts.length) {
              set(
                (state) => ({ status: state.items.length ? LbStatus.FINISHED : LbStatus.EMPTY }),
                false,
                'loadPosts - Loaded',
              );
              return;
            }

            const newItems = [...items, ...loadedPosts].toSorted(sortFunction);
            set({ items: newItems, lastVersion: result.version, status: LbStatus.OK }, false, 'loadPosts');
          } catch {
            set(() => ({ status: LbStatus.ERROR }), false, 'loadPosts - Error');
          }
        },
        loadUpdates: async (service: BasePollingService) => {
          const { items, lastVersion } = get();
          const result = await service.getUpdates(lastVersion);
          const debug = service.options?.debug ? logger.error : () => undefined;

          result?.items?.forEach((update) => {
            debug(`Received post ${update.action} event`, update);
          });

          if (!result) return;

          let newItems = [...items];
          if (result.items?.length) {
            result.items.forEach((update) => {
              newItems = processUpdate(newItems, update);
            });

            newItems.sort(sortFunction);
            set(() => ({ items: newItems }), false, 'loadUpdates');
          }

          set(() => ({ lastVersion: result.version }), false, 'loadUpdates');
        },
      }),
      { name: `Liveblog-${initial.blogId}` },
    ),
  );
